import { FeatureToggleCategory } from 'app/generated/graphql'
// Human-readable metadata that DECORATES the code-generated FEATURE_TOGGLE_NAMES
// list (app/generated/featureToggleNames.ts). That generated list stays the
// source of truth for WHICH toggles exist; this map only adds label/description/
// category. featureToggleMetadata.spec.ts fails if a generated name is
// missing here, so new toggles never render as bare slugs.
//
// UI-ahead-of-backend: this lives in the frontend for now. It should eventually
// move to a backend-editable catalog so non-engineers can maintain labels
// without a deploy (see spec §"Backend gaps").

import { FEATURE_TOGGLE_NAMES } from 'app/generated/featureToggleNames'
import type { ReleaseSlug } from './featureToggleReleases'

export type ToggleCategory =
  | `Contacts`
  | `Analytics`
  | `Scheduling`
  | `Communications`
  | `Integrations`
  | `Platform`
  | `Internal`

export type ToggleMetadata = {
  label: string
  description: string
  category: ToggleCategory
}

// Required so a new toggle cannot ship without someone deciding its release.
// `null` means decided: no release. Database definitions carry no release,
// which is why this is separate from ToggleMetadata.
export type CodeToggleMetadata = ToggleMetadata & { release: ReleaseSlug | null }

// Keys MUST match slugs in FEATURE_TOGGLE_NAMES exactly (the join key). Using
// `satisfies` (not a type annotation) enforces completeness at compile time: a
// missing name or an orphan/misspelled key is a compile error, while the value
// type stays inferred.
export const FEATURE_TOGGLE_METADATA = {
  'action-center': {
    label: `Action Center`,
    description: `Revenue-framed home page: lead, hygiene recall and unscheduled treatment queues, plus the communications that need a reply.`,
    category: `Platform`,
    release: `next-app`,
  },
  'ai-agent-config-route': {
    label: `AI Agent Config Route`,
    description: `Access to the AI voice-agent configuration route.`,
    category: `Communications`,
    release: null,
  },
  'ai-agent-only-notifications': {
    label: `AI Agent Only Notifications`,
    description: `Notifications scoped to AI-agent-only mode.`,
    category: `Communications`,
    release: null,
  },
  'dark-mode': {
    label: `Dark Mode`,
    description: `Dark theme, following the user's appearance preference.`,
    category: `Platform`,
    release: `next-app`,
  },
  dni: {
    label: `Dynamic Number Insertion`,
    description: `DNI call tracking numbers.`,
    category: `Integrations`,
    release: null,
  },
  'facebook-leads-personal-token': {
    label: `Facebook Leads Personal Token`,
    description: `Use a personal token for Facebook lead ingestion.`,
    category: `Integrations`,
    release: null,
  },
  'global-search': {
    label: `Global Search`,
    description: `Ctrl+K command palette that searches contacts, activities and pages from anywhere.`,
    category: `Platform`,
    release: `next-app`,
  },
  'grouped-navigation': {
    label: `Grouped Navigation`,
    description: `Sidebar grouped into Work, Analytics, Records and Lists sections, with Settings pinned at the bottom.`,
    category: `Platform`,
    release: null,
  },
  'inbox-email': {
    label: `Inbox Email`,
    description: `Staff send 1:1 email with attachments from the inbox reply box.`,
    category: `Communications`,
    release: null,
  },
  'incoming-call-alerts': {
    label: `Incoming Call Alerts`,
    description: `Screen-pop card in the alert row the moment a CallRail call starts ringing, with what Trackable knows about the caller.`,
    category: `Communications`,
    release: `next-app`,
  },
  'integrated-mode': {
    label: `Integrated Mode`,
    description: `Integrated (embedded) application mode.`,
    category: `Platform`,
    release: null,
  },
  'needs-action-date': {
    label: `Needs-Action Date`,
    description: `Needs-action date field and surfacing on contacts.`,
    category: `Contacts`,
    release: null,
  },
  paperwork: {
    label: `Paperwork`,
    description: `New patient paperwork: the forms and packets builder under Settings > Forms, and the Documents tab on the contact.`,
    category: `Communications`,
    release: null,
  },
  'pms-comm-log': {
    label: `PMS Comm Log`,
    description: `Comm Log tab in location settings: the practice maps each kind of Trackable activity onto a channel the PMS accepts, and Trackable writes those entries into the patient chart.`,
    category: `Integrations`,
    release: `next-app`,
  },
  'related-contacts-prototype': {
    label: `Related Contacts prototype`,
    description: `Stakeholder demo of families and the primary contact: grouped Active Leads, the reach-through drawer and a simulated workflow. Read-only; nothing is saved or sent.`,
    category: `Contacts`,
    release: `related-contacts`,
  },
  'revenue-dashboard': {
    label: `Revenue Dashboard`,
    description: `Revenue analytics dashboard.`,
    category: `Analytics`,
    release: `next-app`,
  },
  'scheduled-sms': {
    label: `Scheduled SMS`,
    description: `Send a manual text later, on the practice clock.`,
    category: `Communications`,
    release: `next-app`,
  },
  'scheduling-agent': {
    label: `SMS Agent`,
    description: `The AI SMS agent answers patient replies at locations with the SMS Agent product. Off hides the workflow "Hand off to AI agent" block.`,
    category: `Communications`,
    release: null,
  },
  'TRK-2569': {
    label: `TRK-2569`,
    description: `Experimental toggle tracked under Jira TRK-2569.`,
    category: `Internal`,
    release: null,
  },
  'TRK-2637': {
    label: `TRK-2637`,
    description: `Experimental toggle tracked under Jira TRK-2637.`,
    category: `Internal`,
    release: null,
  },
  'TRK-3672': {
    label: `TRK-3672`,
    description: `Experimental toggle tracked under Jira TRK-3672.`,
    category: `Internal`,
    release: null,
  },
  'bulk-sms': {
    label: `Bulk SMS`,
    description: `Send SMS to multiple contacts at once.`,
    category: `Communications`,
    release: `next-app`,
  },
  'workflows-builder': {
    label: `Workflows Builder`,
    description: `Canvas-based automation workflow builder.`,
    category: `Platform`,
    release: `next-app`,
  },
} satisfies Record<(typeof FEATURE_TOGGLE_NAMES)[number], CodeToggleMetadata>

export function getToggleMetadata(name: string): CodeToggleMetadata | undefined {
  return (FEATURE_TOGGLE_METADATA as Record<string, CodeToggleMetadata>)[name]
}

export const CATEGORY_OPTIONS: ToggleCategory[] = [
  `Contacts`,
  `Analytics`,
  `Scheduling`,
  `Communications`,
  `Integrations`,
  `Platform`,
  `Internal`,
]

// The GraphQL FeatureToggleCategory enum serializes as UPPERCASE keys over the
// wire (CONTACTS, ANALYTICS, …) even though the display/DB value is title-case
// (verified against the type-graphql-built schema). These map between the two:
// title-case for the UI, UPPERCASE for GraphQL input/output.
export function categoryToWire(category: ToggleCategory): FeatureToggleCategory {
  return Object.values(FeatureToggleCategory).find((value) => value === category.toUpperCase())!
}

export function categoryFromWire(wire?: string | null): ToggleCategory | undefined {
  if (!wire) return undefined
  return CATEGORY_OPTIONS.find((c) => c.toUpperCase() === wire.toUpperCase())
}
